import supabase from "artifact/supabase";
import { info } from "element/input";
import connected from "standard/connected";
import define from "standard/define";
import on from "standard/on";
import paint from "standard/paint";
import repaint from "standard/repaint";
import trait from "standard/trait";
import willPaint from "standard/willPaint";
import style from "./style";

/**
 * Classe que representa o componente de onboarding.
 *
 * @class
 * @extends HTMLElement
 *
 * @description
 * O componente de onboarding exibe uma lista de escolas para o usuário selecionar
 * e prosseguir para o dashboard da escola escolhida.
 *
 * @example
 * <m-onboarding></m-onboarding>
 */
@define("m-onboarding")
class Onboarding extends HTMLElement {
  /** @type {string|null} ID da escola selecionada. */
  #school = null;

  /** @type {Array<Object>} Lista de equipes associadas ao usuário. */
  #teams = [];

  /** @type {boolean} Indicador de seleção inválida. */
  #invalid = false;

  /**
   * Cria uma instância de Onboarding e configura o Shadow DOM.
   */
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  /**
   * Método chamado antes de pintar o componente.
   * Busca as equipes associadas ao usuário na base de dados.
   *
   * @async
   * @method
   * @memberof Onboarding
   *
   * @returns {Promise<Onboarding>} A instância atual do componente.
   *
   * @description
   * Este método é executado antes da renderização do componente, buscando
   * os dados das equipes associadas ao usuário, os quais são necessários para
   * preencher a lista de escolas.
   */
  @willPaint
  async [trait.fetch]() {
    const { data: teams } = await supabase
      .from("school")
      .select("*, project(name), team!inner(school_id, user_id)")
      .eq("team.user_id", this.dataset.user);

    this.#teams = teams || [];
    return this;
  }

  /**
   * Método chamado quando o componente é conectado ao DOM.
   * Renderiza o template do componente.
   *
   * @method
   * @memberof Onboarding
   *
   * @returns {string} O template HTML do componente.
   *
   * @description
   * Renderiza a lista de escolas com base nos dados de equipes obtidos.
   * Adiciona também a mensagem de suporte e o botão para continuar.
   */
  @connected
  @paint
  [trait.template]() {
    const schoolItems = this.#teams
      .map(
        (school) => `
        <li class='${style.onboarding__li}'>
          <label for='_${school.id}' class='${style.onboarding__label}'>
            <input type='radio' name='school' id='_${school.id}' value='${school.id}' />
            <div>
              <span class='${style.onboarding__span}'>${school.fantasy_name}</span><br />
              <strong class='${style.onboarding__strong}'>${school.project.name}</strong>
            </div>
          </label>
        </li>
      `,
      )
      .join("");

    return `
      <section class='${style.onboarding}'>
        <ul class='${style.onboarding__ul}'>
          ${schoolItems}
        </ul>
        <span class='${style.onboarding__supporting}' invalid="${this.#invalid}">
          ${info()} Escolha uma escola para continuar
        </span>
        <button class='${style.onboarding__button}'>Continuar</button>
      </section>
    `;
  }

  /**
   * Método chamado quando um input de tipo radio é alterado.
   * Atualiza a escola selecionada.
   *
   * @method
   * @memberof Onboarding
   *
   * @param {Event} event - O evento de mudança disparado pelo input.
   *
   * @returns {Onboarding} A instância atual do componente.
   *
   * @description
   * Atualiza o valor da escola selecionada com base no valor do input
   * que foi alterado.
   */
  @on.change("input")
  [trait.change](event) {
    this.#school = event.target.value;
    return this;
  }

  /**
   * Método chamado ao clicar no botão de continuar.
   * Redireciona o usuário para o dashboard da escola selecionada.
   *
   * @method
   * @memberof Onboarding
   *
   * @param {Event} event - O evento de clique disparado pelo botão.
   *
   * @returns {Onboarding} A instância atual do componente.
   *
   * @description
   * Se uma escola foi selecionada, o usuário é redirecionado para o dashboard
   * da escola. Caso contrário, o indicador de seleção inválida é ativado.
   */
  @on.click("button")
  @repaint
  [trait.click]() {
    if (this.#school) {
      location.assign(`/app/${this.dataset.user}/${this.#school}/dashboard`);
      return this;
    }

    this.#invalid = true;
    return this;
  }
}

export default Onboarding;
